import React, { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box, Button, Chip, Divider, Link, TextField, Typography,
  Alert, InputAdornment, IconButton, CircularProgress, Tooltip,
} from '@mui/material';
import { Visibility, VisibilityOff, MarkEmailRead, WorkspacePremium } from '@mui/icons-material';
import GitHubIcon from '@mui/icons-material/GitHub';
import { PasswordStrengthIndicator } from '../../components/common/PasswordStrengthIndicator';
import { useRegisterMutation, useVerifyPaymentSessionMutation } from '../../store/api/authApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8009';

const schema = yup.object({
  fullName: yup.string().trim().required('Full name is required'),
  email: yup.string().email('Enter a valid email address').required('Email address is required'),
  password: yup
    .string()
    .min(8, 'At least 8 characters')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

type FormData = yup.InferType<typeof schema>;

// Component-scoped input styles for sign-up form fields to prevent global CSS impact
const signUpFieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: 'rgba(7, 19, 33, 0.65)',
    color: '#F8FAFC',
    fontSize: '0.9375rem',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(120, 180, 220, 0.3)',
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(120, 180, 220, 0.5)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(7, 19, 33, 0.9)',
      boxShadow: '0 0 0 3px rgba(56, 189, 248, 0.25)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#38BDF8',
        borderWidth: '1px',
      },
    },
    '&.Mui-error': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#EF4444',
      },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.25)',
      },
    },
    '& input': {
      color: '#F8FAFC',
    },
    '& input::placeholder': {
      color: '#94A3B8',
      opacity: 1,
    },
  },
  '& .MuiFormHelperText-root': {
    color: '#EF4444',
    marginLeft: '4px',
    marginTop: '4px',
  },
};

export const SignUpPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isPaidFlow = !!sessionId;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [proActivated, setProActivated] = useState(false);

  const [register, { isLoading }] = useRegisterMutation();
  const [verifyPaymentSession] = useVerifyPaymentSessionMutation();

  const { register: formRegister, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const password = watch('password', '');

  const onSubmit = async (data: FormData) => {
    setServerError('');
    try {
      await register({ email: data.email, password: data.password, full_name: data.fullName }).unwrap();
      setRegisteredEmail(data.email);

      // If user came from Stripe checkout, upgrade their plan
      if (sessionId) {
        try {
          await verifyPaymentSession({ session_id: sessionId, email: data.email }).unwrap();
          setProActivated(true);
        } catch (payErr: any) {
          console.error('Payment verification failed:', payErr?.data?.detail ?? payErr);
          setServerError(
            'Account created but we could not confirm your payment automatically. Please contact support or try logging in.'
          );
        }
      }

      setRegistered(true);
    } catch (err: any) {
      setServerError(err?.data?.detail ?? 'Registration failed. Please try again.');
    }
  };

  if (registered) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <MarkEmailRead sx={{ fontSize: 56, color: '#39D6B0', mb: 2 }} />
        <Typography variant="h4" fontWeight={800} sx={{ color: '#F8FAFC', letterSpacing: '-0.02em', mb: 1 }}>
          Check your email
        </Typography>
        {proActivated && (
          <Chip
            icon={<WorkspacePremium fontSize="small" sx={{ color: '#39D6B0 !important' }} />}
            label="Pro plan activated"
            sx={{ mb: 2, fontWeight: 700, bgcolor: 'rgba(57, 214, 176, 0.12)', color: '#39D6B0', border: '1px solid rgba(57, 214, 176, 0.3)' }}
          />
        )}
        <Typography variant="body2" sx={{ color: '#94A3B8', mb: 3 }}>
          We sent a verification link to <strong style={{ color: '#F8FAFC' }}>{registeredEmail}</strong>.
          Click the link to activate your account.
        </Typography>
        <Box sx={{
          bgcolor: 'rgba(7, 19, 33, 0.65)', border: '1px solid rgba(120, 180, 220, 0.25)',
          borderRadius: '12px', p: 2.5, mb: 3, textAlign: 'left'
        }}>
          {['Click the link in your email', 'You\'ll be signed in automatically', 'Connect GitHub to start scanning'].map((step, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%', bgcolor: 'rgba(57, 214, 176, 0.16)', color: '#39D6B0',
                border: '1px solid rgba(57, 214, 176, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <Typography variant="caption" fontWeight={700}>{i + 1}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#94A3B8' }}>{step}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" sx={{ color: '#94A3B8' }}>
          Already verified?{' '}
          <Link component={RouterLink} to="/auth/login" sx={{ color: '#39D6B0', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Sign in
          </Link>
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ color: '#F8FAFC', letterSpacing: '-0.02em', mb: 0.75 }}>
        Create your account
      </Typography>
      <Typography variant="body2" sx={{ color: '#94A3B8', textAlign: 'center', mb: isPaidFlow ? 1.5 : 3.5 }}>
        Start analysing your codebase in minutes
      </Typography>

      {isPaidFlow && (
        <Alert severity="success" icon={<WorkspacePremium sx={{ color: '#10B981' }} />} sx={{ mb: 2.5, bgcolor: 'rgba(16, 185, 129, 0.12)', color: '#10B981', border: '1px solid rgba(16, 185, 129, 0.28)', borderRadius: '12px' }}>
          Payment received — your account will be activated on the <strong>Pro plan</strong>.
        </Alert>
      )}

      {serverError && (
        <Alert severity="error" sx={{ mb: 2.5, bgcolor: 'rgba(239, 68, 68, 0.12)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.28)', borderRadius: '12px' }}>
          {serverError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Full Name */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 0.75 }}>
            Full name
          </Typography>
          <TextField
            {...formRegister('fullName')}
            placeholder="Jane Doe"
            fullWidth
            autoComplete="name"
            autoFocus
            error={!!errors.fullName}
            helperText={errors.fullName?.message}
            sx={signUpFieldSx}
          />
        </Box>

        {/* Work Email */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 0.75 }}>
            Work email
          </Typography>
          <TextField
            {...formRegister('email')}
            placeholder="jane@company.com"
            type="email"
            fullWidth
            autoComplete="email"
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={signUpFieldSx}
          />
        </Box>

        {/* Password */}
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 0.75 }}>
            Password
          </Typography>
          <TextField
            {...formRegister('password')}
            placeholder="At least 8 characters"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            autoComplete="new-password"
            error={!!errors.password}
            helperText={errors.password?.message}
            sx={signUpFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword ? 'Hide password' : 'Show password'} arrow placement="top">
                    <IconButton
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      sx={{
                        width: 38,
                        height: 38,
                        color: '#94A3B8',
                        borderRadius: '8px',
                        transition: 'color 0.15s ease, background-color 0.15s ease',
                        '&:hover': {
                          color: '#F8FAFC',
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&:focus-visible': {
                          color: '#38BDF8',
                          backgroundColor: 'rgba(56, 189, 248, 0.12)',
                          outline: '2px solid #38BDF8',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <PasswordStrengthIndicator password={password} />

        {/* Confirm Password */}
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 0.75 }}>
            Confirm password
          </Typography>
          <TextField
            {...formRegister('confirmPassword')}
            placeholder="Repeat password"
            type={showConfirm ? 'text' : 'password'}
            fullWidth
            autoComplete="new-password"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            sx={signUpFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showConfirm ? 'Hide password' : 'Show password'} arrow placement="top">
                    <IconButton
                      type="button"
                      onClick={() => setShowConfirm((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      sx={{
                        width: 38,
                        height: 38,
                        color: '#94A3B8',
                        borderRadius: '8px',
                        transition: 'color 0.15s ease, background-color 0.15s ease',
                        '&:hover': {
                          color: '#F8FAFC',
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&:focus-visible': {
                          color: '#38BDF8',
                          backgroundColor: 'rgba(56, 189, 248, 0.12)',
                          outline: '2px solid #38BDF8',
                        },
                      }}
                    >
                      {showConfirm ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Submit CTA */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            mt: 3,
            mb: 2.5,
            py: 1.4,
            fontWeight: 700,
            fontSize: '0.95rem',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #39D6B0 0%, #20C878 100%)',
            color: '#071321',
            boxShadow: '0 2px 10px rgba(57, 214, 176, 0.25)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4ce0bd 0%, #2bd987 100%)',
              boxShadow: '0 4px 16px rgba(57, 214, 176, 0.4)',
            },
          }}
        >
          {isLoading ? <CircularProgress size={22} sx={{ color: '#071321' }} /> : 'Create account'}
        </Button>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: 'rgba(120, 180, 220, 0.14)' }}>
        <Typography variant="caption" sx={{ color: '#64748B', px: 1 }}>OR</Typography>
      </Divider>

      {/* GitHub OAuth Button */}
      <Button
        fullWidth
        variant="outlined"
        size="large"
        startIcon={<GitHubIcon sx={{ color: '#F8FAFC' }} />}
        onClick={() => { window.location.href = `${API_BASE_URL}/auth/github`; }}
        sx={{
          py: 1.25,
          fontSize: '0.9rem',
          fontWeight: 600,
          borderColor: 'rgba(120, 180, 220, 0.25)',
          color: '#F8FAFC',
          bgcolor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '10px',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(120, 180, 220, 0.4)',
          },
        }}
      >
        Sign up with GitHub
      </Button>

      <Typography variant="body2" textAlign="center" sx={{ mt: 3.5, color: '#94A3B8' }}>
        Already have an account?{' '}
        <Link
          component={RouterLink}
          to="/auth/login"
          sx={{ color: '#39D6B0', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Sign in
        </Link>
      </Typography>
    </Box>
  );
};

export default SignUpPage;
